import {
    Component,
    OnInit,
    OnChanges,
    SimpleChanges,
    Input,
    Output,
    EventEmitter,
    inject,
    signal,
    OnDestroy,
    computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SopService } from '../../../core/services/sop.service';
import { EntityCacheService } from '../../../core/services/entity-cache.service';
import { SopResponseDto } from '@qc/shared';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';

@Component({
    selector: 'qc-sop-selector',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        InputTextModule,
        DropdownModule,
        ButtonModule,
        DialogModule,
    ],
    template: `
        <div class="form-field">
            <label class="block text-sm mb-1">
                {{ label }}
                <span *ngIf="required">*</span>
            </label>
            <div *ngIf="!selectedSop()" class="space-y-3">
                <p-dropdown
                    [options]="filteredSops"
                    [(ngModel)]="selectedSop"
                    optionLabel="sopNumber"
                    placeholder="Select a SOP..."
                    (onChange)="onSopSelected($event)"
                    (onShow)="onDropdownShow()"
                    [showClear]="true"
                    [filter]="true"
                    filterBy="sopNumber,sopTitle"
                    (onFilter)="onSearchChange($event.filter)"
                    styleClass="w-full"
                    panelStyleClass="dropdown-panel"
                    scrollHeight="200px"
                    [class.border-rose-500]="hasValidationError()"
                    [disabled]="disabled"
                    [loading]="isSearching"
                >
                    <ng-template let-sop pTemplate="item">
                        <div class="p-3">
                            <div class="font-medium text-gray-900">{{ sop.sopNumber }}</div>
                            <div class="text-sm text-gray-500 mt-1" *ngIf="sop.sopTitle">
                                {{ sop.sopTitle }}
                            </div>
                            <div class="text-xs text-gray-400 mt-1" *ngIf="sop.managedBy">
                                Managed by: {{ sop.managedBy }}
                            </div>
                        </div>
                    </ng-template>
                </p-dropdown>
                <div *ngIf="showCreateButton" class="text-center mt-3">
                    <p-button
                        severity="secondary"
                        variant="outlined"
                        (click)="openCreateModal()"
                        icon="pi pi-plus"
                        label="Add New SOP"
                        size="small"
                        [disabled]="disabled"
                    />
                </div>
            </div>
            <div *ngIf="selectedSop()" class="flex justify-between items-start border border-gray-200 rounded-md p-3">
                <div>
                    <div class="font-medium text-gray-900">{{ selectedSop()?.sopNumber }}</div>
                    <div class="text-sm text-gray-500 mt-1" *ngIf="selectedSop()?.sopTitle">
                        {{ selectedSop()?.sopTitle }}
                    </div>
                    <div class="text-xs text-gray-400 mt-1" *ngIf="selectedSop()?.managedBy">
                        Managed by: {{ selectedSop()?.managedBy }}
                    </div>
                </div>
                <p-button
                    severity="secondary"
                    variant="text"
                    (click)="clearSelection()"
                    icon="pi pi-times"
                    size="small"
                    [disabled]="disabled"
                />
            </div>
            <div *ngIf="hasValidationError()" class="text-rose-500 text-sm mt-1">
                {{ getValidationError() }}
            </div>
        </div>
    `,
})
export class SopSelectorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() label: string = 'SOP';
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() showCreateButton: boolean = true;
    @Input() initialValue: Partial<SopResponseDto> | null = null;
    @Output() sopSelected = new EventEmitter<SopResponseDto>();
    @Output() sopCleared = new EventEmitter<void>();
    @Output() validationChange = new EventEmitter<boolean>();

    private sopService = inject(SopService);
    private entityCacheService = inject(EntityCacheService);
    private destroy$ = new Subject<void>();
    private searchTerms$ = new BehaviorSubject<string>('');

    selectedSop = signal<SopResponseDto | null>(null);
    allSops = signal<SopResponseDto[]>([]);
    filteredSops: SopResponseDto[] = [];
    isSearching = false;

    touched = signal<boolean>(false);

    private loadedSopId: string | null = null;

    isValid = computed(() => {
        if (!this.required) return true;
        return !!this.selectedSop();
    });

    hasValidationError = computed(() => {
        return this.required && this.touched() && !this.selectedSop();
    });

    ngOnInit(): void {
        this.setupSearch();
        this.handleInitialValue();
        this.emitValidationChange();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialValue']) {
            this.handleInitialValue();
        }
        if (changes['required']) {
            this.emitValidationChange();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupSearch(): void {
        this.searchTerms$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((searchTerm) => {
                    this.isSearching = true;
                    return this.sopService.getAllSops();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (result: SopResponseDto[]) => {
                    this.filteredSops = result;
                    this.isSearching = false;
                },
                error: (error: any) => {
                    this.isSearching = false;
                },
            });
    }

    private loadInitialSops(): void {
        this.sopService
            .getAllSops()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result: SopResponseDto[]) => {
                    this.allSops.set(result);
                    this.filteredSops = result;
                },
                error: (error: any) => {},
            });
    }

    onSearchChange(searchTerm: string): void {
        this.searchTerms$.next(searchTerm);
    }

    onDropdownShow(): void {
        if (this.allSops().length === 0) {
            this.loadInitialSops();
        }
    }

    onSopSelected(event: any): void {
        this.touched.set(true);
        const sop = event.value;
        if (sop) {
            this.selectedSop.set(sop);
            this.sopSelected.emit(sop);
        } else {
            this.selectedSop.set(null);
            this.sopCleared.emit();
        }
        this.emitValidationChange();
    }

    clearSelection(): void {
        this.selectedSop.set(null);
        this.sopCleared.emit();
        this.emitValidationChange();
    }

    openCreateModal(): void {
        this.touched.set(true);
    }

    private handleInitialValue(): void {
        if (this.initialValue) {
            if (this.initialValue.sopNumber) {
                this.selectedSop.set(this.initialValue as SopResponseDto);
            } else if (this.initialValue.id && this.loadedSopId !== this.initialValue.id) {
                this.loadSopById(this.initialValue.id);
            }
        } else {
            this.selectedSop.set(null);
            this.loadedSopId = null;
        }
        this.emitValidationChange();
    }

    private loadSopById(sopId: string): void {
        this.loadedSopId = sopId;
        this.sopService
            .getSopById(sopId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (sop: SopResponseDto) => {
                    this.selectedSop.set(sop);
                    this.sopSelected.emit(sop);
                    this.emitValidationChange();
                },
                error: (error: any) => {
                    this.selectedSop.set(null);
                    this.emitValidationChange();
                },
            });
    }

    getValidationError(): string {
        if (!this.hasValidationError()) return '';
        return 'Please select a SOP from the list or create a new one';
    }

    markAsTouched(): void {
        this.touched.set(true);
        this.emitValidationChange();
    }

    private emitValidationChange(): void {
        this.validationChange.emit(this.isValid());
    }
}
